import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { EMPTY, Subject, catchError, debounceTime, firstValueFrom, forkJoin, of, switchMap, takeUntil } from "rxjs";
import * as _ from "lodash";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import { IProduct } from "./models/product.model";
import { Status } from "src/app/models/statusEnums";
import { ProductTotalCalculationService } from "./service/products-total-calculation.service";
import { IOrderTotalCalculationContext, IProductCalculationContext } from "./models/product-calculation-context.model";
import { TypeIds } from "src/app/inventory/sales-order/constants/constants";
import { SalesOrderService } from "src/app/inventory/sales-order/sales-order.service";
import { UtilityService } from "src/app/inventory/sales-order/utils/utility.service";
import { AppMainService } from "src/app/app-main.service";
import { MessageService } from "primeng/api";
import { FormToProductModelBuilderService } from "./service/form-to-product-model-builder.service";
import { ProductsCalculationUtilsService } from "./service/products-calculation-utils.service";
import { v4 as uuidv4 } from "uuid";
import { ImportserialService } from "../../services/importserial.service";
import { CurrencyService } from "../../services/currency.service";

@Component({
    selector: "app-common-product-add-form",
    templateUrl: "./common-product-add-form.component.html",
    styleUrls: ["./common-product-add-form.component.scss"],
})
export class CommonProductAddFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) checkedFields: any;
    @Input({ required: true }) pricebookConfig: any;
    @Input({ required: true }) moduleName: any;
    @Input({ required: true }) loggedUserDetails: any;
    @Input({ required: true }) savedProductOrderModel: any;
    @Input() accountDetails: any;
    @Input() baseCurrency: any;
    @Input() currencyExchangeRate: number = 1;
    @Input() previousCurrencyExchangeRate: number = 0;
    @Input() currencyUnit: number;
    @Input() recalculate: { value: boolean } = { value: false };

    @Output() onCancel = new EventEmitter();
    @Output() productEventEmitter = new EventEmitter();

    protected ngUnsubscribe$: Subject<void> = new Subject<void>();

    productForm: FormGroup;
    addedProducts: IProduct[] = [];
    isDisplayProductDialog: boolean = false;
    headerText = "Product List";
    isVisibleLocation = true;
    selectedLocation: any;
    selectedLocationId = 0;
    isBundleProductAddition: boolean = false;
    selectedBundleIndex: number = 0;
    pTypeId = TypeIds;

    /** Discount  */
    discountOptions = [];
    additionalDiscountOptions = [];
    discountEnabled = false;

    /** Tax */
    taxSettings: any = null;
    allTaxRates: any[] = [];
    taxConfig: any = null;
    taxEnabled: boolean = false;

    /** Additional Fees */
    additionalFees = [];
    lineLevelHandlingFees = [];
    footerLevelHandlingFees = [];   

    /** Account */
    accountInfo: any;
    billingAddresss: any;
    addressInfo: any;

    // fraction digits
    fractionDigits: number = 0;
    qtyFractionDigits: number = 0;

    bProductFieldsToDisable = ["itemNo", "description", "uomId", "totalQuantity", "price", "taxAmount", "discountType", "discountRate", "discountAmount", "handlingFeeType", "handlingFeeRate", "handlingFeeAmount"];
    kitProductFieldsToDisable = ["itemNo", "description", "uomId", "qty", "totalQuantity", "price", "totalAmount", "taxAmount", "subTotal", "baseSubTotal", "discountType", "discountRate", "discountAmount", "handlingFeeType", "handlingFeeRate", "handlingFeeAmount"];

    isBundleExpanded = true;
    cols = 2;
    currencySymbol: string = "";
    isFormChanged: boolean = false;

    decimalPlacesMap: { [key: number]: number } = {};
    measurementUnits  = [];

    constructor(
        private formBuilder: FormBuilder,
        private productTotalCalculationService: ProductTotalCalculationService,
        private salesOrderService: SalesOrderService,
        private utilityService: UtilityService,
        private appMainService: AppMainService,
        private messageService: MessageService,
        private formToProductModelBuilderService: FormToProductModelBuilderService,
        private productsCalculationUtilsService: ProductsCalculationUtilsService,
        private importService: ImportserialService,
        private currencyService: CurrencyService,
    ) {}


    async ngOnInit() {
        this.currencySymbol = this.currencyService.currencySymbolValue;
        this.fetchUnitOfMeasureCategories();
        this.getFractionDigits();
        this.getQtyDecimalDigits();
        this.initForm();
        await this.loadInitialData();
        this.subscribeToProductFormChanges();
        this.subscribeToProductFormArrayChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.accountDetails) {
            this.accountDetails = changes.accountDetails.currentValue;
            if(this.accountDetails && this.accountDetails.id){
                this.fetchAccountDetails();
            }
        }
        if(this.savedProductOrderModel && this.savedProductOrderModel?.products && this.savedProductOrderModel?.products?.length > 0){
            this.isFormChanged = true;
        }
        if(this.recalculate.value){
            this.recalculateProductsAmount();
        }
    }

    ngOnDestroy(): void {
        if (this.ngUnsubscribe$) {
            this.ngUnsubscribe$.next();
            this.ngUnsubscribe$.complete();
            this.ngUnsubscribe$.unsubscribe();
        }
    }

    initForm() {
        this.productForm = this.formBuilder.group({
            discountType: [],
            discountRate: [0],
            discountAmount: [0],
            taxAmount: [0],
            handlingFeeType: [null],
            handlingFeeRate: [0],
            handlingFeeAmount: [0],
            additionalDiscountType: [null],
            additionalDiscountRate: [0],
            additionalDiscountAmount: [0],
            shippingCharge: [0],
            orderSubtotal: [0],
            orderBaseSubtotal: [0],
            productTotalTaxAmount: [0],
            productTotalDiscountAmount: [0],
            productTotalHandlingFeeAmount: [0],
            productTotalAmount: [0],
            totalAmount: [0],
            products: this.formBuilder.array([]),
        });

        this.productForm.get("taxAmount")?.disable();
        this.productForm.get("discountAmount")?.disable();
        this.productForm.get("handlingFeeAmount")?.disable();
        this.productForm.get("additionalDiscountAmount")?.disable();
        this.productForm.get("additionalDiscountAmount")?.disable();
        this.productForm.get("totalAmount")?.disable();

        this.getColsCount();

    }

    getFractionDigits() {
        this.importService
            .getDecimalValue()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: (value) => {
                    if (value) {
                        this.fractionDigits = value;
                    }
                },
                error: (error) => { },
            });
    }
    getQtyDecimalDigits() {
        this.importService
            .getQtyDecimalValue()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((value) => {
                if (value) {
                    this.qtyFractionDigits = value;
                }
            });
    }

    async loadInitialData() {
        this.appMainService.isShowSpinner("loadData");
        const taxObservable = this.salesOrderService.getAllTax().pipe(catchError((error) => this.handleError("Payment Term", error)));
        const additionalDiscountObservable = this.salesOrderService.getDiscounts(false).pipe(catchError((error) => this.handleError("Discount", error)));
        const additionalFeesObservable = this.salesOrderService.getAdditionalFees().pipe(catchError((error) => this.handleError("Additional Fees", error)));
        const discountConfigObservable = this.salesOrderService.getAllDiscount().pipe(catchError((error) => this.handleError("Discount", error)));

        try {
            const [taxData, additionalDiscounts, additionalFees, discountConfig] = await firstValueFrom(forkJoin([taxObservable, additionalDiscountObservable, additionalFeesObservable, discountConfigObservable]));
            this.setTaxConfig(taxData);
            this.setAdditionalDiscounts(additionalDiscounts);
            this.setAdditionalFees(additionalFees);
            this.setDiscountConfig(discountConfig);

            if (this.savedProductOrderModel) {
                this.setFormValues();
            }
        } catch (error) {
            this.appMainService.isHideSpinner("loadData");
            this.messageService.add({ severity: "error", summary: "Error", detail: "Error occurred..." });
        } finally {
            this.appMainService.isHideSpinner("loadData");
        }
    }

    private handleError(apiName: string, error: any) {
        this.appMainService.isHideSpinner("loadData");
        this.messageService.add({ severity: "error", summary: "Error", detail: `Error occurred with ${apiName}: ${error}` });
        return of(null);
    }

    async fetchAccountDetails() {
        // this.accountInfo = await this.salesOrderService.getAccountDetailsById(this.selectedAccount?.id);
        this.accountInfo = this.accountDetails;
        if (this.accountInfo && this.accountInfo.accountAddresses && this.accountInfo.accountAddresses.length > 0) {
            const allBillAddress = this.accountInfo.accountAddresses.filter((a) => a.address?.addressType?.name?.toLowerCase() == "billing");
            this.billingAddresss = allBillAddress ? allBillAddress.find((b) => b.address?.isDefault) : null;
        }
        await this.setAddressInfo();
    }
    async setAddressInfo() {
        this.addressInfo = {
            countryId: this.billingAddresss?.address?.countryId ?? 0,
            stateId: this.billingAddresss?.address?.stateId ?? 0,
            zipCode: this.billingAddresss?.address?.zipCode ?? "",
        };
        await this.getTaxByAddress(this.addressInfo?.countryId);
    }
    async getTaxByAddress(countryId: number) {
        if (!countryId) return;

        const taxData: any = await this.utilityService.getTaxByCountryId(countryId);
        this.taxSettings = taxData.taxSettings ?? null;
        this.allTaxRates = taxData.allTaxRates ?? [];
        this.taxConfig = taxData.taxConfig ?? null;
    }

    /** Subscribe to Product Form value changes for total section */
    
    subscribeToProductFormChanges() {
        this.productForm?.valueChanges.pipe(takeUntil(this.ngUnsubscribe$), debounceTime(300)).subscribe(() => {
            this.isFormChanged = true;
            this.checkAndUpdateRateField("discountType", "discountRate");
            this.checkAndUpdateRateField("additionalDiscountType", "additionalDiscountRate");
            this.checkAndUpdateRateField("handlingFeeType", "handlingFeeRate");

            this.calculateOrderTotal();
        });
    }
    checkAndUpdateRateField(dropdownControlName: string, rateControlName: string) {
        const dropdownControl = this.productForm.get(dropdownControlName);
        const rateControl = this.productForm.get(rateControlName);

        if (dropdownControl?.dirty && dropdownControl?.touched) {
            const discountValue = dropdownControl?.value?.discountValue;
            this.patchRateField(rateControlName, discountValue > 100 ? 100 : discountValue);
            dropdownControl.markAsPristine();
        } else {
            if(dropdownControl?.value?.type?.name == "Amount" || dropdownControl?.value?.type?.name == "$"){
                this.patchRateField(rateControlName, rateControl?.value);
            }
            else if ((dropdownControl?.value?.type?.name == "Percentage" || dropdownControl?.value?.type?.name == "%") && rateControl?.value > 100) {
                this.patchRateField(rateControlName, 100);
            } else {
                this.patchRateField(rateControlName, rateControl?.value);
            }
        }
    }
    patchRateField(controlName: string, newValue: any) {
        const control = this.productForm.get(controlName);
        if (control) {
            control.patchValue(newValue, { onlySelf: true, emitEvent: false });
        }
    }

    /** End Subscribe to Product Form value changes for total section */

    /** Subscribe to Product Array Form Changes*/

    subscribeToProductFormArrayChanges() {
        const productsArray = this.productForm.get("products") as FormArray;
        productsArray.valueChanges
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                debounceTime(300),
                switchMap((changedProducts) => {
                    changedProducts.forEach((product, index) => {
                        if(product.typeId === TypeIds.BundleTypeId){
                            this.updateBundleParentProductsAmount(product, index);
                        } else {
                            this.checkAndUpdateProductDiscountRate(product, index);
                            this.checkAndUpdateProductHandlingFeeRate(product, index);
                            this.calculateProductTotalAsync(product, index);
                        }
                    });
                    this.isFormChanged = true;
                    this.calculateOrderTotal();
                    return EMPTY;
                }),
            )
            .subscribe();
    }
    updateBundleParentProductsAmount(product, productIndex: number) {
        let totalBaseSubTotal = 0;
        let totalAmount = 0;
      
        if (product && product?.bundleProducts?.length > 0) {
          product?.bundleProducts.forEach(item => {
            totalBaseSubTotal += item.baseSubTotal ?? 0;
            totalAmount += item.totalAmount ?? 0;
          });
        }
        const baseSubTotalControl = this.getProductByIndex(productIndex).get("baseSubTotal");
        const totalAmountControl = this.getProductByIndex(productIndex).get("totalAmount");
        baseSubTotalControl.patchValue(totalBaseSubTotal, { onlySelf: true, emitEvent: false });
        totalAmountControl.patchValue(totalAmount, { onlySelf: true, emitEvent: false });

    }
    checkAndUpdateProductDiscountRate(product: any, productIndex: number) {
        const discountTypeControl = this.getProductByIndex(productIndex).get("discountType");
        const discountRateControl = this.getProductByIndex(productIndex).get("discountRate");

        if (discountTypeControl?.dirty && discountTypeControl?.touched) {
            const discountValue = discountTypeControl.value?.discountValue;
            if (discountValue) {
                const cappedDiscountValue = discountValue > 100 ? 100 : discountValue;
                product.discountRate = cappedDiscountValue;
                this.patchProductDiscountRate(productIndex, cappedDiscountValue);
            } else {
                this.patchProductDiscountRate(productIndex, 0);
            }
            discountTypeControl.markAsPristine();
        } else {
            if(discountTypeControl?.value?.type?.name == "Amount" || discountTypeControl?.value?.type?.name == "$"){
                this.patchProductDiscountRate(productIndex, discountRateControl?.value);
            }
            else if ((discountTypeControl?.value?.type?.name == "Percentage" || discountTypeControl?.value?.type?.name == "%") && discountRateControl?.value > 100) {
                this.patchProductDiscountRate(productIndex, 100);
            } else {
                this.patchProductDiscountRate(productIndex, discountRateControl?.value);
            }
        }
    }
    patchProductDiscountRate(productIndex: number, newValue: any) {
        const productForm = this.getProductByIndex(productIndex);
        const discountRateControl = productForm.get("discountRate");
        if (discountRateControl) {
            discountRateControl.patchValue(newValue, { onlySelf: true, emitEvent: false });
        }
    }
    checkAndUpdateProductHandlingFeeRate(product: any, productIndex: number) {
        const handlingFeeTypeControl = this.getProductByIndex(productIndex).get("handlingFeeType");
        const handlingFeeRateControl = this.getProductByIndex(productIndex).get("handlingFeeRate");

        if (handlingFeeTypeControl?.dirty && handlingFeeTypeControl?.touched) {
            const handlingFeeValue = handlingFeeTypeControl.value?.discountValue;
            if (handlingFeeValue) {
                const cappedHandlingFeeValue = handlingFeeValue > 100 ? 100 : handlingFeeValue;
                product.handlingFeeRate = cappedHandlingFeeValue;
                this.patchProductHandlingFeeRate(productIndex, cappedHandlingFeeValue);
            } else {
                this.patchProductHandlingFeeRate(productIndex, 0);
            }
            handlingFeeTypeControl.markAsPristine();
        } else {
            if(handlingFeeTypeControl?.value?.type?.name == "Amount" || handlingFeeTypeControl?.value?.type?.name == "$"){
                this.patchProductHandlingFeeRate(productIndex, handlingFeeRateControl?.value);
            } else if ((handlingFeeTypeControl?.value?.type?.name == "Percentage" || handlingFeeTypeControl?.value?.type?.name == "%") && handlingFeeRateControl?.value > 100) {
                this.patchProductHandlingFeeRate(productIndex, 100);
            } else {
                this.patchProductHandlingFeeRate(productIndex, handlingFeeRateControl?.value);
            }
        }
    }
    patchProductHandlingFeeRate(productIndex: number, newValue: any) {
        const productForm = this.getProductByIndex(productIndex);
        const handlingFeeRateControl = productForm.get("handlingFeeRate");
        if (handlingFeeRateControl) {
            handlingFeeRateControl.patchValue(newValue, { onlySelf: true, emitEvent: false });
        }
    }
    calculateProductTotalAsync(changedProduct: any, updatedProductIndex: number) {
        const productCalculationContext: IProductCalculationContext = {
            product: changedProduct,
            allTaxRates: this.allTaxRates,
            zipcode: this.addressInfo?.zipCode ?? "",
            stateId: this.addressInfo?.stateId ?? "",
            taxSettings: this.taxSettings,
            taxConfig: this.taxConfig,
            taxEnabled: this.taxEnabled,
            isVisibleRowTax: this.checkedFields?.tax?.enabledInForm ?? false,
        };
        this.productTotalCalculationService.calculateProductTotal(productCalculationContext, false, false);
        this.patchProductToForm(updatedProductIndex, productCalculationContext.product);
    }
    patchProductToForm(updatedProductIndex: number, updatedProduct: any) {
        const productForm = this.getProductByIndex(updatedProductIndex);
        productForm.patchValue(
            {
                baseSubTotal: updatedProduct?.baseSubTotal ?? 0,
                taxAmount: updatedProduct?.taxAmount ?? 0,
                totalAmount: updatedProduct?.totalAmount ?? 0,
                discountAmount: updatedProduct?.discountAmount ?? 0,
                handlingFeeAmount: updatedProduct?.handlingFeeAmount ?? 0,
            },
            { onlySelf: true, emitEvent: false },
        );
    }

    /** End Subscribe to Product Array Form Changes*/

    /** Subscribe to Bundle Child Products Form Array value Cahnges */

    subscribeToBundleProductsFormArrayChanges(bundleProductsControl: FormArray) {
        bundleProductsControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe$), debounceTime(300)).subscribe((changedBundles) => {
            this.isFormChanged = true;
            changedBundles.forEach((bundle) => {
                const { uuid } = bundle;
                const productIndex = this.getProductIndexForBundle(uuid);
                this.checkAndUpdateBundleDiscountRate(bundle, productIndex, uuid);
                this.checkAndUpdateBundleHandlingFeeRate(bundle, productIndex, uuid);
                this.calculateBundleTotalAsync(bundle, productIndex, uuid);
            });

            this.calculateOrderTotal();
        });
    }
    
    checkAndUpdateBundleDiscountRate(bundle: any, productIndex: number, bundleUuid: string) {
        const discountTypeControl = this.getBundleByUuid(bundleUuid)?.get("discountType");
        const discountRateControl = this.getBundleByUuid(bundleUuid)?.get("discountRate");

        if (discountTypeControl?.dirty && discountTypeControl?.touched) {
            const discountValue = discountTypeControl.getRawValue()?.discountValue;
            if (discountValue) {
                const cappedDiscountValue = discountValue > 100 ? 100 : discountValue;
                bundle.discountRate = cappedDiscountValue;
                this.patchBundleDiscountRate(bundleUuid, cappedDiscountValue);
            } else {
                this.patchBundleDiscountRate(bundleUuid, 0);
            }
            discountTypeControl.markAsPristine();
        } else {
            if (discountRateControl?.getRawValue()?.value > 100) {
                this.patchBundleDiscountRate(bundleUuid, 100);
            }
        }
    }
    patchBundleDiscountRate(bundleUuid: string, newValue: any) {
        const bundleForm = this.getBundleByUuid(bundleUuid);
        const discountRateControl = bundleForm?.get("discountRate");
        if (discountRateControl) {
            discountRateControl.patchValue(newValue, { onlySelf: true, emitEvent: false });
        }
    }
    checkAndUpdateBundleHandlingFeeRate(bundle: any, productIndex: number, bundleUuid: string) {
        const handlingFeeTypeControl = this.getBundleByUuid(bundleUuid)?.get("handlingFeeType");
        const handlingFeeRateControl = this.getBundleByUuid(bundleUuid)?.get("handlingFeeRate");

        if (handlingFeeTypeControl?.dirty && handlingFeeTypeControl?.touched) {
            const handlingFeeValue = handlingFeeTypeControl.getRawValue()?.discountValue;
            if (handlingFeeValue) {
                const cappedHandlingFeeValue = handlingFeeValue > 100 ? 100 : handlingFeeValue;
                bundle.handlingFeeRate = cappedHandlingFeeValue;
                this.patchBundleHandlingFeeRate(bundleUuid, cappedHandlingFeeValue);
            } else {
                this.patchBundleHandlingFeeRate(bundleUuid, 0);
            }
            handlingFeeTypeControl.markAsPristine();
        } else {
            if (handlingFeeRateControl?.getRawValue()?.value > 100) {
                this.patchBundleHandlingFeeRate(bundleUuid, 100);
            }
        }
    }
    patchBundleHandlingFeeRate(bundleUuid: string, newValue: any) {
        const bundleForm = this.getBundleByUuid(bundleUuid);
        const handlingFeeRateControl = bundleForm?.get("handlingFeeRate");
        if (handlingFeeRateControl) {
            handlingFeeRateControl.patchValue(newValue, { onlySelf: true, emitEvent: false });
        }
    }
    getProductIndexForBundle(bundleUuid: string): number {
        const productsArray = this.productForm.get("products") as FormArray;
        let productIndex = -1;

        productsArray.controls.forEach((productControl, index) => {
            const bundleProductsControl = productControl.get("bundleProducts") as FormArray;

            bundleProductsControl.controls.forEach((bundleControl) => {
                const bundle = bundleControl.getRawValue();
                if (bundle.uuid === bundleUuid) {
                    productIndex = index;
                }
            });
        });

        return productIndex;
    }
    calculateBundleTotalAsync(changedBundleProduct: any, updatedProductIndex: number, bundleUuid: string) {
        const bundleCalculationContext: IProductCalculationContext = {
            product: changedBundleProduct,
            allTaxRates: this.allTaxRates,
            zipcode: this.addressInfo?.zipCode ?? "",
            stateId: this.addressInfo?.stateId ?? "",
            taxSettings: this.taxSettings,
            taxConfig: this.taxConfig,
            taxEnabled: this.taxEnabled,
            isVisibleRowTax: this.checkedFields?.tax?.enabledInForm ?? false,
        };

        this.productTotalCalculationService.calculateProductTotal(bundleCalculationContext, false, true);

        const bundleForm = this.getBundleByUuid(bundleUuid);
        if (bundleForm) {
            bundleForm.patchValue(
                {
                    baseSubTotal: bundleCalculationContext?.product?.baseSubTotal ?? 0,
                    taxAmount: bundleCalculationContext?.product?.taxAmount ?? 0,
                    totalAmount: bundleCalculationContext?.product?.totalAmount,
                    discountAmount: bundleCalculationContext?.product?.discountAmount,
                    handlingFeeAmount: bundleCalculationContext?.product?.handlingFeeAmount,
                },
                { onlySelf: true, emitEvent: false },
            );
        }
    }

    getBundleByUuid(bundleUuid: string): FormGroup {
        const productsArray = this.productForm.get("products") as FormArray;

        for (let productControl of productsArray.controls) {
            const bundleProductsControl = productControl.get("bundleProducts") as FormArray;

            for (let bundleControl of bundleProductsControl.controls) {
                const bundle = bundleControl.getRawValue();
                if (bundle.uuid === bundleUuid) {
                    return bundleControl as FormGroup;
                }
            }
        }

        return null;
    }

    /** End Subscribe to Bundle Child Products Form Array value Cahnges */

    addProduct(addedProduct: IProduct = null) {
        const control = <FormArray>this.productForm.get("products");
        control.push(this.initProduct(addedProduct));
    }

    removeProduct(index: number) {
        const control = <FormArray>this.productForm.get("products");
        control.removeAt(index);
        const productCount = control.controls.length;
        if(productCount == 0){
            this.productForm.patchValue(
                {
                    handlingFeeType: null,
                    handlingFeeRate: 0,
                    additionalDiscountType: null,
                    additionalDiscountRate: 0,
                    shippingCharge: 0,
                    totalAmount: 0,
                },
                { onlySelf: true, emitEvent: false },
            );
        }
    }

    getProductByIndex(index: number) {
        const control = <FormArray>this.productForm.get("products");
        return control.controls[index];
    }

    initProduct(addedProduct: IProduct = null) {
        let formGroup = this.formBuilder.group({
            id: [addedProduct?.id ?? 0],
            uuid: [uuidv4()],
            createdSource: [addedProduct?.createdSource],
            updatedSource: [addedProduct?.updatedSource],
            createdBy: [addedProduct?.createdBy],
            updatedBy: [addedProduct?.updatedBy],
            createdDate: [addedProduct?.createdDate],
            updatedDate: [addedProduct?.updatedDate],
            productId: [addedProduct?.productId ?? null],
            itemNo: [addedProduct?.itemNo ?? ""],
            description: [addedProduct?.description ?? ""],
            uomList: [addedProduct?.uom ?? []],
            uomId: [addedProduct?.uomId ?? null],
            unit: [addedProduct?.unit ?? null],
            qty: [addedProduct?.qty ?? 1],
            totalQuantity: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.totalQuantity ?? 1)],
            price: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.price ?? 0)],
            totalAmount: [addedProduct?.totalAmount ?? 0],
            taxAmount: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.taxAmount ?? 0)],
            subTaxes: [addedProduct?.typeId === TypeIds.BundleTypeId ? [] : (addedProduct?.subTaxes ?? [])],
            totalCostPrice: [addedProduct?.totalCostPrice ?? 0],
            costPrice: [addedProduct?.costPrice ?? 0],
            subTotal: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.subTotal ?? 0)],
            baseSubTotal: [addedProduct?.baseSubTotal ?? 0],
            statusId: [Status.Active],
            warehouseId: [addedProduct?.warehouseId ?? null],
            typeId: [addedProduct?.typeId ?? 0],
            parentTypeId: [addedProduct?.typeId ?? 0],
            discountType: [addedProduct?.typeId === TypeIds.BundleTypeId ? null : (addedProduct?.discountType ?? null)],
            discountRate: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.discountRate ?? 0)],
            discountAmount: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.discountAmount ?? 0)],
            handlingFeeType: [addedProduct?.typeId === TypeIds.BundleTypeId ? null : (addedProduct?.handlingFeeType ?? null)],
            handlingFeeRate: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.handlingFeeRate ?? 0)],
            handlingFeeAmount: [addedProduct?.typeId === TypeIds.BundleTypeId ? 0 : (addedProduct?.handlingFeeAmount ?? 0)],
            bundleProducts: this.formBuilder.array([]),
        });

        // Disable Bundle Product Fields
        if (addedProduct?.typeId === TypeIds.BundleTypeId) {
            this.disableBundleProductFields(formGroup);
        }

        if (addedProduct?.bundleDetails && addedProduct.bundleDetails.length > 0) {
            const bundleProductsControl = formGroup.get("bundleProducts") as FormArray;
            addedProduct.bundleDetails.forEach((bundle) => {
                let bundleProductFormGroup = this.formBuilder.group({
                    id: [bundle?.id ?? 0],
                    uuid: [uuidv4()],
                    createdSource: [bundle?.createdSource],
                    updatedSource: [bundle?.updatedSource],
                    createdBy: [bundle?.createdBy],
                    updatedBy: [bundle?.updatedBy],
                    createdDate: [bundle?.createdDate],
                    updatedDate: [bundle?.updatedDate],
                    productId: [bundle?.productId ?? null],
                    itemNo: [bundle?.itemNo ?? ""],
                    description: [bundle?.description ?? ""],
                    uomList: [bundle?.uom ?? []],
                    uomId: [bundle?.uomId ?? null],
                    unit: [bundle?.unit ?? null],
                    qty: [bundle?.qty ?? 1],
                    totalQuantity: [bundle?.totalQuantity ?? 1],
                    price: [bundle?.price ?? 0],
                    totalAmount: [bundle?.totalAmount ?? 0],
                    taxAmount: [bundle?.taxAmount ?? 0],
                    subTaxes: [bundle?.subTaxes ?? []],
                    totalCostPrice: [bundle?.totalCostPrice ?? 0],
                    costPrice: [bundle?.costPrice ?? 0],
                    subTotal: [bundle?.subTotal ?? 0],
                    baseSubTotal: [bundle?.baseSubTotal ?? 0],
                    statusId: [Status.Active],
                    typeId: [bundle?.typeId ?? 0],
                    parentTypeId: [addedProduct?.typeId ?? 0],
                    discountType: [bundle?.discountType ?? null],
                    discountRate: [bundle?.discountRate ?? 0],
                    discountAmount: [bundle?.discountAmount ?? 0],
                    handlingFeeType: [bundle?.handlingFeeType ?? null],
                    handlingFeeRate: [bundle?.handlingFeeRate ?? 0],
                    handlingFeeAmount: [bundle?.handlingFeeAmount ?? 0],
                    warehouseId: [bundle?.warehouseId ?? null],
                });
                
                // Disable Bundle Product Fields
                if (addedProduct?.typeId === TypeIds.KitTypeId) {
                    this.disableKitProductFields(bundleProductFormGroup);
                }

                bundleProductsControl.push(bundleProductFormGroup);
            });

            // Subscribe to changes for each bundle product
            this.subscribeToBundleProductsFormArrayChanges(bundleProductsControl);
        }
        return formGroup;
    }

    disableBundleProductFields(formGroup: FormGroup) {
        this.bProductFieldsToDisable.forEach((field) => {
            formGroup.get(field)?.disable({ emitEvent: false });
        });
    }

    disableKitProductFields(formGroup: FormGroup) {
        this.kitProductFieldsToDisable.forEach((field) => {
            formGroup.get(field)?.disable({ emitEvent: false });
        });
    }

    getBundleProducts(itemIndex: number): FormArray {
        const productControl = (this.productForm.get("products") as FormArray)?.at(itemIndex) as FormGroup;
        return <FormArray>productControl.get("bundleProducts");
    }

    removeBundleProduct(itemIndex: number, bundleIndex: number): void {
        const productFormArray = this.productForm.get("products") as FormArray;
        const bundleProductFormArray = productFormArray.at(itemIndex).get("bundleProducts") as FormArray;
        bundleProductFormArray.removeAt(bundleIndex);
    }

    onProductAddComplete(event) {
        this.isDisplayProductDialog = false;
        if (this.isBundleProductAddition) {
            this.handleBundleProductAddition(event);
        } else {
            this.handleProductAddition(event);
        }
    }

    handleProductAddition(event) {
        event = event ? event : [];
        let newProducts = [];
        const products = this.productForm.value.products ? this.productForm.value.products : [];

        event.map((item) => {
            let product = products.find((x) => x.productId == item.id);
            product == null && newProducts.push(item);
        });

        products.map((addedProduct, index) => {
            let product = event.find((x) => x.id == addedProduct.productId);
            if (!product) this.removeProduct(index);
        });

        newProducts.map((x) => {
            const productCalculationContext: IProductCalculationContext = {
                product: x,
                allTaxRates: this.allTaxRates,
                zipcode: this.addressInfo?.zipCode ?? "",
                stateId: this.addressInfo?.stateId ?? "",
                taxSettings: this.taxSettings,
                taxConfig: this.taxConfig,
                taxEnabled: this.taxEnabled,
                isVisibleRowTax: this.checkedFields?.tax?.enabledInForm ?? false,
            };

            if (x.typeId === TypeIds.BundleTypeId || x.typeId === TypeIds.KitTypeId) {
                this.productTotalCalculationService.calculateProductTotal(productCalculationContext, true, false);
            } else {
                this.productTotalCalculationService.calculateProductTotal(productCalculationContext, false, false);
            }

            let addedProductId: number = 0;

            let bundleProducts = [];
            productCalculationContext?.product?.bundleDetails?.map((bundle) => {
                let qty = bundle.unit && bundle.unit.uomDetail.value ? bundle.unit.uomDetail.value : 0;
                let price = bundle.price ? parseFloat(bundle.price) : 0;
                const addedProduct: IProduct = {
                    id: addedProductId,
                    createdSource: this.loggedUserDetails.userIP,
                    updatedSource: this.loggedUserDetails.userIP,
                    createdBy: this.loggedUserDetails.userId,
                    updatedBy: this.loggedUserDetails.userId,
                    createdDate: new Date().toISOString(),
                    updatedDate: new Date().toISOString(),
                    productId: bundle.id,
                    itemNo: bundle.itemNo,
                    description: bundle.description,
                    uomId: bundle?.unit?.uomDetail ? x.unit?.uomDetail?.id : null,
                    uom: bundle.uom ?? [],
                    unit: bundle.unit ? bundle.unit : null,
                    qty: bundle?.qty ?? 1,
                    price: Number(price) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    totalAmount: Number(bundle?.totalAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    statusId: Status.Active,
                    warehouseId: this.selectedLocation?.id ?? 0,
                    totalQuantity: bundle.totalQuantity ?? 0,
                    totalCostPrice: Number(bundle.totalCostPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    costPrice: Number(bundle.costPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    subTotal: Number(bundle.subTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    baseSubTotal: Number(bundle.baseSubTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    typeId: bundle.typeId,
                    discountOptions: this.discountOptions ?? [],
                    additionalDiscountOptions: this.additionalDiscountOptions ?? [],
                    additionalFees: this.additionalFees ?? [],
                    subTaxes: bundle.subTaxes ?? [],
                    taxAmount: Number(bundle.taxAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                    discountType: null,
                    discountRate: 0,
                    discountAmount: 0,
                    handlingFeeType: null,
                    handlingFeeRate: 0,
                    handlingFeeAmount: 0,
                };
                bundleProducts.push(addedProduct);
            });

            const addedProduct: IProduct = {
                id: addedProductId,
                createdSource: this.loggedUserDetails.userIP,
                updatedSource: this.loggedUserDetails.userIP,
                createdBy: this.loggedUserDetails.userId,
                updatedBy: this.loggedUserDetails.userId,
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString(),
                productId: x.id,
                itemNo: x.itemNo,
                description: x.description,
                uomId: x?.unit?.uomDetail ? x.unit?.uomDetail?.id : null,
                uom: x.uom ?? [],
                unit: x.unit ? x.unit : null,
                qty: x.qty ?? 1,
                price: Number(x.price ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                totalAmount: Number(x.totalAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                statusId: Status.Active,
                warehouseId: this.selectedLocation?.id ?? 0,
                bundleDetails: bundleProducts,
                totalQuantity: x.totalQuantity ?? 0,
                totalCostPrice: Number(x.totalCostPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                costPrice: Number(x.costPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                subTotal: Number(x.subTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                baseSubTotal: Number(x.baseSubTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                typeId: x.typeId,
                discountOptions: this.discountOptions ?? [],
                additionalDiscountOptions: this.additionalDiscountOptions ?? [],
                additionalFees: this.additionalFees ?? [],
                subTaxes: x.subTaxes ?? [],
                taxAmount: Number(x.taxAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                discountType: null,
                discountRate: 0,
                discountAmount: 0,
                handlingFeeType: null,
                handlingFeeRate: 0,
                handlingFeeAmount: 0,
            };
            this.addProduct(addedProduct);
        });
        this.setAddedProducts();
    }

    handleBundleProductAddition(event) {
        const productControl = this.productForm.get("products") as FormArray;
        const productGroup = productControl.at(this.selectedBundleIndex) as FormGroup;
        const bundleProductsControl = productGroup.get("bundleProducts") as FormArray;
        let newBundleProducts = [];

        event.forEach((item) => {
            const existingBundleProduct = bundleProductsControl.controls.find((bundleProduct) => bundleProduct.value.productId === item.id);
            if (!existingBundleProduct) {
                newBundleProducts.push(item);
            }
        });

        bundleProductsControl.controls.map((addedProduct, index) => {
            const productId = addedProduct.value.productId;
            let productInEvent = event.find((x) => x.id === productId);
            if (!productInEvent) {
                bundleProductsControl.removeAt(index);
            }
        });

        newBundleProducts.forEach((product) => {
            const productCalculationContext: IProductCalculationContext = {
                product: product,
                allTaxRates: this.allTaxRates,
                zipcode: this.addressInfo?.zipCode ?? "",
                stateId: this.addressInfo?.stateId ?? "",
                taxSettings: this.taxSettings,
                taxConfig: this.taxConfig,
                taxEnabled: this.taxEnabled,
                isVisibleRowTax: this.checkedFields?.tax?.enabledInForm ?? false,
            };
            this.productTotalCalculationService.calculateProductTotal(productCalculationContext, false, true);
            const addedProduct: IProduct = {
                id: 0,
                createdSource: this.loggedUserDetails.userIP,
                updatedSource: this.loggedUserDetails.userIP,
                createdBy: this.loggedUserDetails.userId,
                updatedBy: this.loggedUserDetails.userId,
                createdDate: new Date().toISOString(),
                updatedDate: new Date().toISOString(),
                productId: product.id,
                itemNo: product.itemNo,
                description: product.description,
                uomId: product?.unit?.uomDetail ? product.unit?.uomDetail?.id : null,
                uom: product.uom ?? [],
                unit: product.unit ? product.unit : null,
                qty: product.qty ?? 1,
                price: Number(product.price ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                totalAmount: Number(product.totalAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                statusId: Status.Active,
                warehouseId: this.selectedLocation?.id ?? 0,
                bundleDetails: [],
                totalQuantity: product.totalQuantity ?? 0,
                totalCostPrice: Number(product.totalCostPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                costPrice: Number(product.costPrice ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                subTotal: Number(product.subTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                baseSubTotal: Number(product.baseSubTotal ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                typeId: product.typeId,
                discountOptions: this.discountOptions ?? [],
                additionalDiscountOptions: this.additionalDiscountOptions ?? [],
                additionalFees: this.additionalFees ?? [],
                subTaxes: product.subTaxes ?? [],
                taxAmount: Number(product.taxAmount ?? 0) * (this.currencyExchangeRate ?? this.baseCurrency?.exchangeRate),
                discountType: null,
                discountRate: 0,
                discountAmount: 0,
                handlingFeeType: null,
                handlingFeeRate: 0,
                handlingFeeAmount: 0,
            };

            bundleProductsControl.push(
                this.formBuilder.group({
                    id: [addedProduct?.id ?? 0],
                    uuid: [uuidv4()],
                    createdSource: [addedProduct?.createdSource],
                    updatedSource: [addedProduct?.updatedSource],
                    createdBy: [addedProduct?.createdBy],
                    updatedBy: [addedProduct?.updatedBy],
                    createdDate: [addedProduct?.createdDate],
                    updatedDate: [addedProduct?.updatedDate],
                    productId: [addedProduct?.productId ?? null],
                    itemNo: [addedProduct?.itemNo ?? ""],
                    description: [addedProduct?.description ?? ""],
                    uomList: [addedProduct?.uom ?? []],
                    uomId: [addedProduct?.uomId ?? null],
                    unit: [addedProduct?.unit ?? null],
                    qty: [addedProduct?.qty ?? 1],
                    totalQuantity: [addedProduct?.totalQuantity ?? 1],
                    price: [addedProduct?.price ?? 0],
                    totalAmount: [addedProduct?.totalAmount ?? 0],
                    taxAmount: [addedProduct?.taxAmount ?? 0],
                    subTaxes: [addedProduct?.subTaxes ?? []],
                    totalCostPrice: [addedProduct?.totalCostPrice ?? 0],
                    costPrice: [addedProduct?.costPrice ?? 0],
                    subTotal: [addedProduct?.subTotal ?? 0],
                    baseSubTotal: [addedProduct?.baseSubTotal ?? 0],
                    statusId: [Status.Active],
                    warehouseId: [addedProduct?.warehouseId ?? null],
                    typeId: [addedProduct?.typeId ?? 0],
                    parentTypeId: [addedProduct?.typeId ?? 0],
                    discountType: [null],
                    discountRate: [0],
                    discountAmount: [0],
                    handlingFeeType: [null],
                    handlingFeeRate: [0],
                    handlingFeeAmount: [0],
                    bundleProducts: this.formBuilder.array([]),
                }),
            );
        });
    }

    setFormValues() {
        const products = this.savedProductOrderModel?.products ?? [];
        products.map((x) => {
            let bundleProducts = [];
            if (x.typeId === TypeIds.BundleTypeId || x.typeId === TypeIds.KitTypeId) {
                x.bundleDetails.map((bundle) => {
                    const addedProduct: IProduct = {
                        id: bundle.id,
                        createdSource: bundle.createdSource,
                        updatedSource: this.loggedUserDetails.userIP,
                        createdBy: bundle.createdBy,
                        updatedBy: this.loggedUserDetails.userId,
                        createdDate: bundle.createdDate,
                        updatedDate: new Date().toISOString(),
                        productId: bundle.productId ?? 0,
                        itemNo: bundle.itemNo ?? "",
                        description: bundle.description ?? "",
                        uomId: bundle.uomId ?? 0,
                        uom: bundle.uom ?? [],
                        unit: bundle.unit ? bundle.unit : null,
                        qty: bundle.qty ?? 1,
                        price: bundle.price ?? 0,
                        totalAmount: bundle.totalAmount ?? 0,
                        statusId: Status.Active,
                        warehouseId: bundle.warehouseId ?? 0,
                        bundleDetails: [],
                        totalQuantity: bundle.totalQuantity ?? 0,
                        totalCostPrice: bundle.totalCostPrice ?? 0,
                        costPrice: bundle.costPrice ?? 0,
                        subTotal: bundle.subTotal ?? 0,
                        baseSubTotal: bundle.baseSubTotal ?? 0,
                        typeId: bundle.typeId ?? 0,
                        discountOptions: this.discountOptions ?? [],
                        additionalDiscountOptions: this.additionalDiscountOptions ?? [],
                        additionalFees: this.additionalFees ?? [],
                        subTaxes: bundle.subTaxes ?? [],
                        taxAmount: this.productsCalculationUtilsService.getTotalTaxAmount(bundle.subTaxes ?? []),

                        discountType: bundle?.discount?.id ? this.discountOptions?.find((v) => v.id == bundle?.discount?.id) : null,
                        discountRate: bundle.discountRate ?? 0,
                        discountAmount: bundle.discountAmount ?? 0,

                        handlingFeeType: bundle.handlingFee?.id ? this.additionalFees?.find((af) => af.id == bundle.handlingFee?.id) : null,
                        handlingFeeRate: bundle.handlingFeeRate ?? 0,
                        handlingFeeAmount: bundle.handlingFeeAmount ?? 0,
                    };
                    bundleProducts.push(addedProduct);
                });
            }

            const addedProduct: IProduct = {
                id: x.id,
                createdSource: x.createdSource,
                updatedSource: this.loggedUserDetails.userIP,
                createdBy: x.createdBy,
                updatedBy: this.loggedUserDetails.userId,
                createdDate: x.createdDate,
                updatedDate: new Date().toISOString(),
                productId: x.productId ?? 0,
                itemNo: x.itemNo ?? "",
                description: x.description ?? "",
                uomId: x.uomId ?? 0,
                uom: x.uom ?? [],
                unit: x.unit ? x.unit : null,
                qty: x.qty ?? 1,
                price: x.price ?? 0,
                totalAmount: x.totalAmount ?? 0,
                statusId: Status.Active,
                warehouseId: x.warehouseId ?? 0,
                bundleDetails: bundleProducts,
                totalQuantity: x.totalQuantity ?? 0,
                totalCostPrice: x.totalCostPrice ?? 0,
                costPrice: x.costPrice ?? 0,
                subTotal: x.subTotal ?? 0,
                baseSubTotal: x.baseSubTotal ?? 0,
                typeId: x.typeId ?? 0,
                discountOptions: this.discountOptions ?? [],
                additionalDiscountOptions: this.additionalDiscountOptions ?? [],
                additionalFees: this.additionalFees ?? [],
                subTaxes: x.subTaxes ?? [],
                taxAmount: this.productsCalculationUtilsService.getTotalTaxAmount(x.subTaxes ?? []),

                discountType: x?.discount?.id ? this.discountOptions?.find((v) => v.id == x?.discount?.id) : null,
                discountRate: x.discountRate ?? 0,
                discountAmount: x.discountAmount ?? 0,

                handlingFeeType: x.handlingFee?.id ? this.additionalFees?.find((af) => af.id == x.handlingFee?.id) : null,
                handlingFeeRate: x.handlingFeeRate ?? 0,
                handlingFeeAmount: x.handlingFeeAmount ?? 0,
            };
            this.addProduct(addedProduct);
        });

        this.patchProductFormValues();
        this.calculateOrderTotal();
    }

    patchProductFormValues() {
        this.productForm.patchValue(
            {
                orderSubtotal: this.savedProductOrderModel?.orderSubtotal ?? 0,
                orderBaseSubtotal: this.savedProductOrderModel?.orderBaseSubtotal ?? 0,
                discountType: this.savedProductOrderModel?.discount?.id ? this.discountOptions?.find((v) => v.id == this.savedProductOrderModel?.discount?.id) : null,
                discountRate: this.savedProductOrderModel?.discountRate ?? 0,
                discountAmount: this.savedProductOrderModel?.discountAmount ?? 0,
                taxAmount: this.savedProductOrderModel?.taxAmount ?? 0,
                handlingFeeType: this.savedProductOrderModel?.handlingFee?.id ? this.additionalFees?.find((af) => af.id == this.savedProductOrderModel?.handlingFee?.id) : null,
                handlingFeeRate: this.savedProductOrderModel?.handlingFeeRate ?? 0,
                handlingFeeAmount: this.savedProductOrderModel?.handlingFeeAmount ?? 0,
                additionalDiscountType: this.savedProductOrderModel?.additionalDiscount?.id ? this.additionalDiscountOptions?.find((a) => a?.id == this.savedProductOrderModel?.additionalDiscount?.discountId) : null,
                additionalDiscountRate: this.savedProductOrderModel?.additionalDiscountRate ?? 0,
                additionalDiscountAmount: this.savedProductOrderModel?.additionalDiscountAmount ?? 0,
                shippingCharge: this.savedProductOrderModel?.shippingCharge ?? 0,
                totalAmount: this.savedProductOrderModel?.totalAmount ?? 0,
            },
            { onlySelf: true, emitEvent: false },
        );
    }

    calculateAndPatchOrderSubTotal() {
        const result = this.productTotalCalculationService.calculateSubtotal(this.productForm);
        this.productForm.patchValue(
            {
                orderBaseSubtotal: result?.totalBaseSubTotal ?? 0,
                orderSubtotal: result?.totalSubTotal ?? 0,
                productTotalTaxAmount: result?.productTotalTaxAmount ?? 0,
                productTotalDiscountAmount: result?.productTotalDiscountAmount ?? 0,
                productTotalHandlingFeeAmount: result?.productTotalHandlingFeeAmount ?? 0,
                productTotalAmount: result?.totalAmount ?? 0
            },
            { onlySelf: true, emitEvent: false },
        );
    }

    calculateOrderTotal() {
        this.calculateAndPatchOrderSubTotal();
        const orderTotalContext: IOrderTotalCalculationContext = {
            productForm: this.productForm,
            isDiscountEnabled: this.discountEnabled,
            isTaxEnabled: this.taxEnabled,
            checkedFields: this.checkedFields,
            discountOptions: this.discountOptions,
        };
        const updatedForm: FormGroup = this.productTotalCalculationService.calculateOrderTotal(orderTotalContext);
        const productModel = this.formToProductModelBuilderService.transformFormToProductModel(updatedForm);
        if(this.isFormChanged){
            this.productEventEmitter.emit(productModel);
            this.isFormChanged = false;
        }
    }

    recalculateProductsAmount(){
        const productsArray = this.productForm.get('products') as FormArray;

        productsArray.controls.forEach(productControl => {
            const price = productControl.get('price')?.value;
            const updatedPrice = this.previousCurrencyExchangeRate !== 0 ? (price * this.currencyExchangeRate) / this.previousCurrencyExchangeRate : price;
            productControl.get('price')?.patchValue(updatedPrice, { emitEvent: true });
    
            const bundleProductsArray = productControl.get('bundleProducts') as FormArray;
            bundleProductsArray.controls.forEach(bundleControl => {
                const bundlePrice = bundleControl.get('price')?.value;
                const updatedBundlePrice = this.previousCurrencyExchangeRate !== 0 ? (bundlePrice * this.currencyExchangeRate) /  this.previousCurrencyExchangeRate : bundlePrice;
                bundleControl.get('price')?.patchValue(updatedBundlePrice, { emitEvent: true });
            });
        });
    }

    openAddProductDialog() {
        this.isBundleProductAddition = false;
        this.isDisplayProductDialog = true;
        this.setAddedProducts();
    }

    openAddBundleProductDialog(bundleIndex: number) {
        this.selectedBundleIndex = bundleIndex;
        this.isBundleProductAddition = true;
        this.isDisplayProductDialog = true;
        this.setAddedBundleProducts();
    }

    cancelProductAdd(event) {
        this.isDisplayProductDialog = false;
        this.setAddedProducts();
    }

    onLocationSelect(e) {
        this.selectedLocation = e;
    }

    setAddedProducts() {
        this.addedProducts = [];
        this.productForm.value.products?.map((addedProduct) => {
            this.addedProducts.push({ ...addedProduct, id: addedProduct.productId });
        });
    }
    setAddedBundleProducts() {
        this.addedProducts = [];
        const products = this.productForm.value.products ?? [];
        const selectedBundleProduct = products[this.selectedBundleIndex];

        if (selectedBundleProduct?.bundleProducts?.length > 0) {
            selectedBundleProduct.bundleProducts.forEach((bundleProduct) => {
                this.addedProducts.push({
                    ...bundleProduct,
                    id: bundleProduct.productId,
                });
            });
        }
    }

    disableAllFields(formGroup: FormGroup, productTypeId: number): void {
        if (productTypeId === TypeIds.BundleTypeId) {
            Object.keys(formGroup.controls).forEach((controlName) => {
                formGroup.get(controlName)?.disable();
            });
        }
    }

    setAdditionalDiscounts(discounts) {
        const lineLevelDiscounts = discounts?.filter((d) => d.discountCategoryId === 1 && d.statusId == 1);
        const footerLevelDiscounts = discounts?.filter((d) => d.discountCategoryId === 2 && d.statusId == 1);
        this.discountOptions = [];
        this.additionalDiscountOptions = [];
        if (discounts && discounts?.length > 0) {
            this.additionalDiscountOptions = footerLevelDiscounts?.map((l) => ({ ...l, additionalDescription: `${l.description}(${l.type.description})`, typeDescription: l.type.description }));
            this.discountOptions = this.utilityService.prepareDiscountOptions(lineLevelDiscounts);
        }
    }

    setAdditionalFees(addFees) {
        this.additionalFees = addFees?.map((l) => ({ ...l, additionalname: `${l.description}(${l.type.description})`, typeDescription: l.type.description }));
        this.lineLevelHandlingFees = this.additionalFees?.filter((d) => d.discountCategoryId === 1);
        this.footerLevelHandlingFees = this.additionalFees?.filter((d) => d.discountCategoryId === 2);
    }

    setDiscountConfig(res) {
        this.discountEnabled = res.item1;
    }

    setTaxConfig(res) {
        this.taxEnabled = res?.item1;
    }

    getVisibleFieldCount(): number {
        let count = 0;
        for (const key in this.checkedFields) {
            if (this.checkedFields[key]?.enabledInForm) {
                count++;
            }
        }
        return count;
    }

    getColumnFlex() {
        const visibleCount = this.getVisibleFieldCount();
        return visibleCount > 0 ? 100 / visibleCount + "%" : "auto";
    }
    getColsCount(){
        this.cols = 2;
        if(this.checkedFields?.itemNo?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.productDescription?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.unit?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.uom?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.price?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.subTotal?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.productDiscountType?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.productDiscountRate?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.tax?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.productHandlingFeesType?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.productHandlingFeesRate?.enabledInForm) this.cols = this.cols + 1;
        if(this.checkedFields?.itemTotal?.enabledInForm) this.cols = this.cols + 1;
    }

    fetchUnitOfMeasureCategories() {
        this.salesOrderService.fetchUnitOfMeasureCategories().subscribe(resp => {
            this.measurementUnits  = resp
        });
    }
    getDecimalPlacesCached(uomList: any[], uomId: number): number {
        const categoryId = uomList?.find(x => x.unitOfMeasureId === uomId)?.uomDetail?.categoryId;
        if (!(categoryId in this.decimalPlacesMap)) {
          this.decimalPlacesMap[categoryId] = this.getDecimalPlaces(categoryId);
        }
        return this.decimalPlacesMap[categoryId];
    }
    getDecimalPlaces(categoryId: number): number {
          return this.measurementUnits?.some(unit => unit.id === categoryId && unit.name?.toLowerCase() === 'count') ? 0 : this.qtyFractionDigits ;
    }
}


